import { useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { IconRefresh, IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import type { ProductVariantDto } from "../../../../api";
import { Banner } from "../../../../components/common/banner";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { Shimmer, ShimmerLoader } from "../../../../components/common/shimmer";
import { Heading, Text } from "../../../../components/common/typography";
import { useVariantComparison } from "../../hooks/useVariantComparison";
import type {
  VariantCategoryWinner,
  VariantComparison,
  VariantWinnerByCategory,
} from "../../data/variant-comparison.types";
import logo from "../../../../assets/images/logo.svg";

const STAR_PATH = "M20,0 L21.5,18.5 L40,20 L21.5,21.5 L20,40 L18.5,21.5 L0,20 L18.5,18.5Z";

const SPARKLE_STARS: { size: number; top: string; left: string; delay: number }[] = [
  { size: 52, top: "12%", left: "92%", delay: 0.0 },
  { size: 32, top: "55%", left: "88%", delay: 1.4 },
  { size: 24, top: "20%", left: "72%", delay: 0.8 },
];

function SparkleStars() {
  return (
    <>
      <style>{`
        @keyframes rec-star-sparkle {
          0%   { opacity: 0;    transform: scale(0.2) rotate(0deg);   }
          20%  { opacity: 0.3;  transform: scale(1)   rotate(45deg);  }
          65%  { opacity: 0.18; transform: scale(1.1) rotate(90deg);  }
          88%  { opacity: 0;    transform: scale(0.6) rotate(135deg); }
          100% { opacity: 0;    transform: scale(0.2) rotate(180deg); }
        }
      `}</style>
      {SPARKLE_STARS.map((s, i) => (
        <svg
          key={i}
          aria-hidden
          viewBox="0 0 40 40"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            width: s.size,
            height: s.size,
            top: s.top,
            left: s.left,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            fill: "none",
            stroke: "var(--color-accent-cyan)",
            strokeWidth: 1.5,
            filter: "blur(3px) drop-shadow(0 0 6px var(--color-accent-cyan-tint))",
            animation: `rec-star-sparkle 3.8s ease-in-out ${s.delay}s infinite`,
          }}
        >
          <path d={STAR_PATH} />
        </svg>
      ))}
    </>
  );
}

const CATEGORY_LABELS: Record<keyof VariantWinnerByCategory, string> = {
  price: "Best price",
  performance: "Best performance",
  value: "Best value",
  build: "Best build",
};

interface Props {
  variants: ProductVariantDto[];
  /**
   * When provided, renders this AI result immediately without hitting the API.
   * Used by the saved-comparison view so reopening a comparison shows the
   * same recommendation the user saw at save time.
   */
  initialComparison?: VariantComparison | null;
  /** Fires when a fresh AI comparison is produced — lets the page offer "Save". */
  onResult?: (comparison: VariantComparison) => void;
}

export function AiComparisonRecommendation({
  variants,
  initialComparison,
  onResult,
}: Props) {
  const variantIds = useMemo(() => variants.map((v) => v.id), [variants]);
  const [focusInput, setFocusInput] = useState("");
  const [submittedFocus, setSubmittedFocus] = useState("");
  const [regenerated, setRegenerated] = useState(false);

  const hasInitial = Boolean(initialComparison) && !regenerated;
  const { data, error, isLoading, mutate } = useVariantComparison(
    hasInitial ? [] : variantIds,
    submittedFocus,
  );

  useEffect(() => {
    if (data?.comparison && onResult) {
      onResult(data.comparison);
    }
  }, [data, onResult]);

  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    variants.forEach((v) => map.set(v.id, v.name ?? `Variant #${v.id}`));
    return map;
  }, [variants]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setRegenerated(true);
    setSubmittedFocus(focusInput.trim());
  };

  const hasFocus = focusInput.trim().length > 0;

  const displayedComparison: VariantComparison | undefined =
    hasInitial ? initialComparison ?? undefined : data?.comparison;

  if (variants.length < 2) return null;

  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite p-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
        // A PDF export is read-only — an unsent Focus form, a loading
        // skeleton, or an error+Retry button aren't "comparison details",
        // so skip the whole card in print unless there's an actual result.
        !displayedComparison && "print:hidden",
      )}
    >
      <SparkleStars />
      <div className="mb-4 flex items-center gap-2">
        <img src={logo} alt="Procurtal" className="h-5" />
        <Text variant="body" tone="body" inline>
          AI recommendation
        </Text>
      </div>

      {/* Hidden while a recommendation is generating: the Focus field and
          the disabled Generate button (plus its "type a focus to enable"
          hint) are not actionable mid-request, and sitting directly above
          the skeleton they read as part of the loading state. They return
          with the result. */}
      {!isLoading && (
        <form onSubmit={onSubmit} className="mb-5 flex flex-col gap-2 print:hidden">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Input
              label="Focus"
              required
              placeholder="e.g. best for gaming, value for money, most portable"
              value={focusInput}
              onChange={(e) => setFocusInput(e.target.value)}
              maxLength={300}
              wrapperClassName="flex-1"
            />
            <Button
              type="submit"
              variant="primary"
              size="md"
              leftIcon={<IconRefresh size={16} />}
              disabled={isLoading || !hasFocus}
              className="!h-[54px] shrink-0"
            >
              {data ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {!hasFocus && (
            <Text variant="bodySm" tone="muted" className="px-1.5">
              Type a focus to enable {data ? "Regenerate" : "Generate"}
            </Text>
          )}
        </form>
      )}

      {isLoading ? (
        <ShimmerLoader
          message="Analyzing specs, pricing, and reviews to pick the best option for you…"
        >
          <div className="flex flex-col gap-4">
            <Shimmer className="h-4 w-11/12" />
            <Shimmer className="h-4 w-9/12" />
            <Shimmer className="h-24 w-full rounded-xl" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Shimmer key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          </div>
        </ShimmerLoader>
      ) : error ? (
        <div className="flex flex-col items-center gap-3">
          <Banner tone="error">Couldn&apos;t generate the AI comparison. Please try again.</Banner>
          <Button variant="secondary" size="sm" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      ) : displayedComparison ? (
        <AiComparisonContent
          comparison={displayedComparison}
          nameById={nameById}
        />
      ) : null}
    </div>
  );
}

interface ContentProps {
  comparison: VariantComparison;
  nameById: Map<number, string>;
}

function AiComparisonContent({ comparison, nameById }: ContentProps) {
  const recommendedName = nameById.get(comparison.recommendation.variantId) ?? "—";

  const categoryEntries = (Object.keys(CATEGORY_LABELS) as (keyof VariantWinnerByCategory)[])
    .map((key) => {
      const winner = comparison.winnerByCategory?.[key];
      return winner ? { key, label: CATEGORY_LABELS[key], winner } : null;
    })
    .filter((entry): entry is { key: keyof VariantWinnerByCategory; label: string; winner: VariantCategoryWinner } => entry !== null);

  return (
    <div className="flex flex-col gap-5">
      <Text variant="body" tone="body">
        {comparison.summary}
      </Text>

      <div className="rounded-xl border border-accentCyan/40 bg-accentCyanTint/40 p-4">
        <Text variant="capsSm" tone="muted" className="mb-1">
          OUR PICK
        </Text>
        <Heading variant="h3" tone="dark" className="mb-1">
          {recommendedName}
        </Heading>
        <Text variant="bodySm" tone="body">
          {comparison.recommendation.reason}
        </Text>
      </div>

      {categoryEntries.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categoryEntries.map(({ key, label, winner }) => (
            <div
              key={key}
              className="rounded-xl border border-borderDefault bg-bgLight p-4"
            >
              <Text variant="capsSm" tone="muted" className="mb-1">
                {label.toUpperCase()}
              </Text>
              <Text variant="bodyBold" tone="dark" className="mb-1">
                {nameById.get(winner.variantId) ?? "—"}
              </Text>
              <Text variant="bodyXs" tone="muted">
                {winner.reason}
              </Text>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {comparison.perProduct.map((entry) => (
          <div
            key={entry.variantId}
            className="rounded-xl border border-borderDefault bg-bgWhite p-4"
          >
            <Text variant="bodyBold" tone="dark" className="mb-1">
              {nameById.get(entry.variantId) ?? `Variant #${entry.variantId}`}
            </Text>
            <Text variant="bodyXs" tone="muted" className="mb-3">
              Best for: {entry.bestFor}
            </Text>

            {entry.pros.length > 0 && (
              <div className="mb-3">
                <div className="mb-1 flex items-center gap-1.5">
                  <IconThumbUp size={14} className="text-accentGreenDark" />
                  <Text variant="bodySmBold" tone="dark" inline>
                    Pros
                  </Text>
                </div>
                <ul className="list-disc space-y-0.5 pl-5">
                  {entry.pros.map((pro, i) => (
                    <li key={i}>
                      <Text variant="bodySm" tone="body" inline>
                        {pro}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {entry.cons.length > 0 && (
              <div>
                <div className="mb-1 flex items-center gap-1.5">
                  <IconThumbDown size={14} className="text-accentError" />
                  <Text variant="bodySmBold" tone="dark" inline>
                    Cons
                  </Text>
                </div>
                <ul className="list-disc space-y-0.5 pl-5">
                  {entry.cons.map((con, i) => (
                    <li key={i}>
                      <Text variant="bodySm" tone="body" inline>
                        {con}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
